import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { toast } from "sonner";
import { Plus, Save, Database, Trash2, Edit3, PowerOff, Power, Search } from "lucide-react";

const EMPTY = { schema_name: "", display_name: "", sap_company_db: "", schema_type: "Organic", is_dwellstone: false, description: "", active: true };

export function SchemaManagement() {
    const [rows, setRows] = useState([]);
    const [editing, setEditing] = useState(null); // null | {... | id}
    const [creating, setCreating] = useState(false);

    const load = async () => {
        try { setRows((await api.get("/admin/schemas")).data); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const save = async (row) => {
        try {
            // DWELLSTONE business rule enforced server-side too.
            if (row.id) {
                await api.put(`/admin/schemas/${row.id}`, row);
                toast.success("Schema updated");
            } else {
                await api.post("/admin/schemas", row);
                toast.success("Schema created");
            }
            setEditing(null); setCreating(false);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const toggle = async (row) => {
        try {
            await api.post(`/admin/schemas/${row.id}/${row.active ? "deactivate" : "activate"}`);
            toast.success(row.active ? "Schema deactivated" : "Schema activated");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const remove = async (row) => {
        if (!confirm(`Delete schema ${row.display_name}? This removes all user assignments.`)) return;
        try { await api.delete(`/admin/schemas/${row.id}`); toast.success("Schema deleted"); await load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="p-6 space-y-4" data-testid="schemas-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="sc-section-title">Administration</div>
                    <h1 className="text-2xl font-semibold tracking-tight">Schema Management</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        SAP HANA company schemas. <span className="font-mono">DWELLSTONE</span> is always Non-Organic and cannot be deleted or deactivated.
                    </p>
                </div>
                <button onClick={() => { setCreating(true); setEditing({ ...EMPTY }); }} className="h-9 inline-flex items-center gap-1 px-3 rounded-md border border-border text-sm hover:bg-secondary" data-testid="schema-create-toggle">
                    <Plus className="w-4 h-4" /> New schema
                </button>
            </div>

            <div className="sc-card">
                <table className="w-full sc-table">
                    <thead>
                        <tr>
                            <th>Display name</th><th>Schema name</th><th>SAP CompanyDB</th><th>Type</th><th>DWS</th><th>Active</th><th>Description</th><th className="text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">No schemas defined yet.</td></tr>}
                        {rows.map((r) => (
                            <tr key={r.id} data-testid={`schema-row-${r.schema_name}`}>
                                <td>{r.display_name}</td>
                                <td className="font-mono">{r.schema_name}</td>
                                <td className="font-mono text-xs">{r.sap_company_db || r.schema_name}</td>
                                <td><StatusBadge status={r.schema_type === "Organic" ? "available" : "draft"} /></td>
                                <td>{r.is_dwellstone ? <span className="text-xs font-medium">YES</span> : "—"}</td>
                                <td><StatusBadge status={r.active ? "active" : "inactive"} /></td>
                                <td className="text-muted-foreground text-xs max-w-md truncate">{r.description}</td>
                                <td className="text-right space-x-1">
                                    <button onClick={() => setEditing({ ...r })} className="text-xs h-7 px-2 rounded border border-border hover:bg-secondary" data-testid={`schema-edit-${r.schema_name}`}>
                                        <Edit3 className="w-3 h-3 inline" /> Edit
                                    </button>
                                    <button onClick={() => toggle(r)} disabled={r.is_dwellstone}
                                        className="text-xs h-7 px-2 rounded border border-border hover:bg-secondary disabled:opacity-40"
                                        data-testid={`schema-toggle-${r.schema_name}`}>
                                        {r.active ? <><PowerOff className="w-3 h-3 inline" /> Deactivate</> : <><Power className="w-3 h-3 inline" /> Activate</>}
                                    </button>
                                    <button onClick={() => remove(r)} disabled={r.is_dwellstone}
                                        className="text-xs h-7 px-2 rounded border border-border text-red-600 hover:bg-red-50 disabled:opacity-40"
                                        data-testid={`schema-delete-${r.schema_name}`}>
                                        <Trash2 className="w-3 h-3 inline" /> Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && (
                <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={() => { setEditing(null); setCreating(false); }}>
                    <div className="sc-card p-5 w-full max-w-lg space-y-3 bg-background" onClick={(e) => e.stopPropagation()} data-testid="schema-edit-modal">
                        <div className="flex items-center gap-2 font-semibold"><Database className="w-4 h-4" /> {editing.id ? "Edit schema" : "New schema"}</div>
                        <div>
                            <label className="text-xs text-muted-foreground">Display name</label>
                            <input className="sc-input mt-1" value={editing.display_name} onChange={(e) => setEditing({ ...editing, display_name: e.target.value })} data-testid="schema-form-display-name" />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground">SAP HANA schema name (immutable after creation)</label>
                            <input className="sc-input mt-1 font-mono" value={editing.schema_name} onChange={(e) => setEditing({ ...editing, schema_name: e.target.value, sap_company_db: editing.sap_company_db || e.target.value })} disabled={!!editing.id} data-testid="schema-form-name" />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground">SAP CompanyDB for Service Layer</label>
                            <input className="sc-input mt-1 font-mono" value={editing.sap_company_db || ""} onChange={(e) => setEditing({ ...editing, sap_company_db: e.target.value })} placeholder={editing.schema_name || "e.g. Z_SBOTEST_DWELLSTONE"} data-testid="schema-form-companydb" />
                            <div className="text-[11px] text-muted-foreground mt-1">Used for SAP Service Layer posting. Usually same as schema name in UAT.</div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs text-muted-foreground">Type</label>
                                <select className="sc-input mt-1"
                                    value={editing.schema_type}
                                    onChange={(e) => setEditing({ ...editing, schema_type: e.target.value })}
                                    disabled={(editing.schema_name || "").toUpperCase() === "DWELLSTONE"}
                                    data-testid="schema-form-type">
                                    <option value="Organic">Organic</option>
                                    <option value="Non-Organic">Non-Organic</option>
                                </select>
                                {(editing.schema_name || "").toUpperCase() === "DWELLSTONE" && (
                                    <div className="text-[11px] text-amber-600 mt-1">DWELLSTONE is always Non-Organic.</div>
                                )}
                            </div>
                            <label className="flex items-center gap-2 text-sm mt-6">
                                <input type="checkbox" checked={!!editing.is_dwellstone} onChange={(e) => setEditing({ ...editing, is_dwellstone: e.target.checked })} disabled={(editing.schema_name || "").toUpperCase() === "DWELLSTONE"} data-testid="schema-form-dws" />
                                Is DWELLSTONE
                            </label>
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground">Description</label>
                            <textarea className="sc-input min-h-[80px] py-2 mt-1" value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="schema-form-description" />
                        </div>
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="schema-form-active" />
                            Active
                        </label>
                        <div className="flex justify-end gap-2 pt-2 border-t border-border">
                            <button onClick={() => { setEditing(null); setCreating(false); }} className="h-9 px-3 rounded border border-border text-sm">Cancel</button>
                            <button onClick={() => save(editing)} className="h-9 px-3 inline-flex items-center gap-1 rounded bg-foreground text-background text-sm" data-testid="schema-form-save">
                                <Save className="w-4 h-4" /> {editing.id ? "Save" : "Create"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export function UserAssignments() {
    const [users, setUsers] = useState([]);
    const [selected, setSelected] = useState(null);
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [q, setQ] = useState("");

    useEffect(() => { (async () => setUsers((await api.get("/users")).data))(); }, []);

    const load = async (u) => {
        setSelected(u);
        try { setData((await api.get(`/admin/users/${u.id}/schema-assignments`)).data); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const toggleSchema = (schemaId) => {
        const cur = data.schema_assignments.find((a) => a.schema_id === schemaId);
        const has = !!cur;
        const next = has
            ? data.schema_assignments.filter((a) => a.schema_id !== schemaId)
            : [...data.schema_assignments, { schema_id: schemaId, active: true }];
        const nextWh = has
            ? data.warehouse_assignments.filter((a) => a.schema_id !== schemaId)
            : data.warehouse_assignments;
        setData({ ...data, schema_assignments: next, warehouse_assignments: nextWh });
    };

    const toggleWh = (schemaId, wh_code) => {
        const has = data.warehouse_assignments.some((a) => a.schema_id === schemaId && a.whs_code === wh_code);
        const next = has
            ? data.warehouse_assignments.filter((a) => !(a.schema_id === schemaId && a.whs_code === wh_code))
            : [...data.warehouse_assignments, { schema_id: schemaId, whs_code: wh_code, active: true }];
        setData({ ...data, warehouse_assignments: next });
    };

    const save = async () => {
        if (!selected || !data) return;
        // Build payload: one entry per assigned schema with that schema's warehouse codes
        const bySchema = {};
        for (const a of data.schema_assignments) bySchema[a.schema_id] = { schema_id: a.schema_id, active: true, warehouse_codes: [] };
        for (const w of data.warehouse_assignments) {
            if (bySchema[w.schema_id]) bySchema[w.schema_id].warehouse_codes.push(w.whs_code);
        }
        setBusy(true);
        try {
            const { data: fresh } = await api.put(`/admin/users/${selected.id}/schema-assignments`, { assignments: Object.values(bySchema) });
            setData(fresh);
            toast.success("Assignments saved");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const shown = users.filter((u) => { const s = q.trim().toLowerCase(); return !s || [u.name, u.email, u.role, u.branch].some((v) => String(v || "").toLowerCase().includes(s)); });

    return (
        <div className="p-6 space-y-4" data-testid="user-assignments-page">
            <div><div className="sc-section-title">Administration</div><h1 className="text-2xl font-semibold tracking-tight">User Schema & Warehouse Assignments</h1></div>

            <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
                <div className="sc-card">
                    <div className="px-3 py-2 border-b border-border text-xs uppercase tracking-wider text-muted-foreground">Users</div>
                    <div className="p-2 border-b border-border">
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <input className="sc-input pl-8 h-8 text-sm" placeholder="Search users…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="user-asgmt-search" />
                        </div>
                    </div>
                    <ul className="max-h-[600px] overflow-y-auto">
                        {shown.length === 0 && <li className="px-3 py-4 text-xs text-muted-foreground text-center">No matching users.</li>}
                        {shown.map((u) => (
                            <li key={u.id}>
                                <button onClick={() => load(u)}
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-secondary ${selected?.id === u.id ? "bg-secondary" : ""}`}
                                    data-testid={`user-asgmt-pick-${u.id}`}>
                                    <div className="font-mono text-xs">{u.email}</div>
                                    <div className="text-xs text-muted-foreground">{u.role} · {u.branch || "—"}</div>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="sc-card">
                    {!selected ? (
                        <div className="p-8 text-center text-sm text-muted-foreground">Select a user to manage their schema & warehouse access.</div>
                    ) : !data ? (
                        <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
                    ) : (
                        <div>
                            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                                <div>
                                    <div className="font-semibold">{selected.name} <span className="text-muted-foreground text-xs">· {selected.role}</span></div>
                                    <div className="text-xs text-muted-foreground font-mono">{selected.email}</div>
                                </div>
                                <button onClick={save} disabled={busy} className="h-9 inline-flex items-center gap-1 px-3 rounded-md bg-foreground text-background text-sm font-medium" data-testid="user-asgmt-save">
                                    <Save className="w-4 h-4" /> Save assignments
                                </button>
                            </div>
                            <div className="p-4 space-y-4">
                                {data.schemas.map((sch) => {
                                    const sa = data.schema_assignments.find((a) => a.schema_id === sch.id);
                                    const hasSchema = !!sa;
                                    return (
                                        <div key={sch.id} className="border border-border rounded-md" data-testid={`user-asgmt-schema-${sch.schema_name}`}>
                                            <label className="flex items-center justify-between gap-3 px-3 py-2 border-b border-border bg-secondary/40">
                                                <span className="flex items-center gap-2 text-sm font-medium">
                                                    <input type="checkbox" checked={hasSchema} onChange={() => toggleSchema(sch.id)} disabled={!sch.active} data-testid={`user-asgmt-toggle-${sch.schema_name}`} />
                                                    {sch.display_name} <span className="font-mono text-xs text-muted-foreground">· {sch.schema_name}</span>
                                                </span>
                                                <span className="text-xs text-muted-foreground">{sch.schema_type}{sch.is_dwellstone ? " · DWS" : ""}{!sch.active ? " · inactive" : ""}</span>
                                            </label>
                                            {hasSchema && (
                                                <SchemaWarehouses
                                                    schema={sch}
                                                    selected={data.warehouse_assignments.filter((a) => a.schema_id === sch.id).map((a) => a.whs_code)}
                                                    onToggle={(code) => toggleWh(sch.id, code)}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function SchemaWarehouses({ schema, selected, onToggle }) {
    const [warehouses, setWarehouses] = useState([]);
    useEffect(() => {
        (async () => {
            try {
                // Admin endpoint /me/assigned-warehouses returns all warehouses for admin; for non-admin admin viewing other user we use the schema directly. Fall back to /me which works since admin sees everything.
                const { data } = await api.get(`/me/assigned-warehouses?schemaId=${schema.id}`);
                setWarehouses(data);
            } catch (_) { setWarehouses([]); }
        })();
    }, [schema.id]);

    return (
        <div className="p-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {warehouses.length === 0 && <div className="text-xs text-muted-foreground col-span-full">No warehouses in this schema.</div>}
            {warehouses.map((w) => {
                const has = selected.includes(w.warehouse_code);
                return (
                    <label key={w.warehouse_code} className="flex items-center gap-2 text-xs cursor-pointer p-2 border border-border rounded">
                        <input type="checkbox" checked={has} onChange={() => onToggle(w.warehouse_code)} data-testid={`user-asgmt-wh-${schema.schema_name}-${w.warehouse_code}`} />
                        <div className="min-w-0">
                            <div className="font-mono">{w.warehouse_code}</div>
                            <div className="text-muted-foreground truncate">{w.display_name || w.name}</div>
                        </div>
                    </label>
                );
            })}
        </div>
    );
}


export function WarehouseManagement() {
    const [schemas, setSchemas] = useState([]);
    const [selectedSchemaId, setSelectedSchemaId] = useState("");
    const [rows, setRows] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(false);
    const [itemGroups, setItemGroups] = useState([]); // SAP item groups for the picker

    const selectedSchema = schemas.find((s) => s.id === selectedSchemaId);
    const groupName = (code) => itemGroups.find((g) => g.code === code)?.name || `#${code}`;

    const loadSchemas = async () => {
        try {
            const { data } = await api.get("/admin/schemas");
            setSchemas(data);
            if (!selectedSchemaId && data.length > 0) {
                setSelectedSchemaId(data[0].id);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const load = async (schemaId = selectedSchemaId) => {
        if (!schemaId) {
            setRows([]);
            return;
        }

        setLoading(true);
        try {
            const { data } = await api.get(`/admin/warehouses?schemaId=${encodeURIComponent(schemaId)}`);
            setRows(data);
        } catch (e) {
            toast.error(formatApiError(e));
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadSchemas(); }, []);

    useEffect(() => {
        if (!selectedSchemaId) { setItemGroups([]); return; }
        load(selectedSchemaId);
        (async () => {
            try {
                const { data } = await api.get(`/admin/item-groups?schemaId=${encodeURIComponent(selectedSchemaId)}`);
                setItemGroups(Array.isArray(data) ? data : []);
            } catch { setItemGroups([]); }
        })();
    }, [selectedSchemaId]);

    const toggleGroup = (code) => setEditing((e) => {
        const cur = e.item_groups || [];
        return { ...e, item_groups: cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code] };
    });

    const save = async () => {
        if (!editing) return;

        try {
            await api.put(
                `/admin/warehouses/${encodeURIComponent(editing.schema_name)}/${encodeURIComponent(editing.warehouse_code)}`,
                {
                    display_name: editing.display_name,
                    short_name: editing.short_name,
                    sort_order: Number(editing.sort_order || 999),
                    active: !!editing.active,
                    item_groups: editing.item_groups || [],
                }
            );
            toast.success("Warehouse display updated");
            setEditing(null);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="p-6 space-y-4" data-testid="warehouse-management-page">
            <div>
                <div className="sc-section-title">Administration</div>
                <h1 className="text-2xl font-semibold tracking-tight">Warehouse Management</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Select a schema first, then manage warehouse display names and the <b>product groups each warehouse carries</b> (used to scope the Inquiry item search). SAP warehouse codes are never changed.
                </p>
            </div>

            <div className="sc-card p-4">
                <div className="grid grid-cols-1 md:grid-cols-[360px_1fr] gap-4 items-end">
                    <div>
                        <label className="text-xs text-muted-foreground">Schema</label>
                        <select
                            className="sc-input mt-1"
                            value={selectedSchemaId}
                            onChange={(e) => setSelectedSchemaId(e.target.value)}
                            data-testid="warehouse-schema-select"
                        >
                            {schemas.map((s) => (
                                <option key={s.id} value={s.id}>
                                    {s.display_name} · {s.schema_name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="text-sm text-muted-foreground">
                        {selectedSchema ? (
                            <div>
                                Managing warehouses for{" "}
                                <span className="font-semibold text-foreground">{selectedSchema.display_name}</span>
                                <span className="font-mono text-xs"> · {selectedSchema.schema_name}</span>
                                <span> · {selectedSchema.schema_type}</span>
                            </div>
                        ) : (
                            <div>Select a schema to begin.</div>
                        )}
                    </div>
                </div>
            </div>

            <div className="sc-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[1000px]">
                        <thead>
                            <tr>
                                <th>Warehouse Code</th>
                                <th>SAP Name</th>
                                <th>Display Name</th>
                                <th>Short Name</th>
                                <th>Item Groups (Inquiry scope)</th>
                                <th className="text-right">Sort</th>
                                <th>Active</th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={8} className="text-center py-8 text-muted-foreground">
                                        Loading warehouses…
                                    </td>
                                </tr>
                            )}

                            {!loading && rows.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="text-center py-8 text-muted-foreground">
                                        No warehouses found for this schema.
                                    </td>
                                </tr>
                            )}

                            {!loading && rows.map((w) => (
                                <tr key={`${w.schema_name}-${w.warehouse_code}`}>
                                    <td className="font-mono">{w.warehouse_code}</td>
                                    <td>{w.name || "—"}</td>
                                    <td className="font-medium">{w.display_name || w.name || "—"}</td>
                                    <td>{w.short_name || w.display_name || "—"}</td>
                                    <td className="text-xs">
                                        {(w.item_groups || []).length === 0
                                            ? <span className="text-muted-foreground">All (unscoped)</span>
                                            : <span>{(w.item_groups || []).slice(0, 4).map(groupName).join(", ")}{(w.item_groups || []).length > 4 ? ` +${w.item_groups.length - 4}` : ""}</span>}
                                    </td>
                                    <td className="sc-num">{w.sort_order ?? 999}</td>
                                    <td><StatusBadge status={w.active ? "active" : "inactive"} /></td>
                                    <td className="text-right">
                                        <button
                                            onClick={() => setEditing({ ...w })}
                                            className="text-xs h-7 px-2 rounded border border-border hover:bg-secondary"
                                            data-testid={`warehouse-edit-${w.schema_name}-${w.warehouse_code}`}
                                        >
                                            <Edit3 className="w-3 h-3 inline" /> Edit
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {editing && (
                <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={() => setEditing(null)}>
                    <div className="sc-card p-5 w-full max-w-lg space-y-3 bg-background" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2 font-semibold">
                            <Database className="w-4 h-4" /> Edit warehouse display
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                                <label className="text-xs text-muted-foreground">Schema</label>
                                <input className="sc-input mt-1 font-mono" value={editing.schema_name} disabled />
                            </div>
                            <div>
                                <label className="text-xs text-muted-foreground">Warehouse Code</label>
                                <input className="sc-input mt-1 font-mono" value={editing.warehouse_code} disabled />
                            </div>
                        </div>

                        <div>
                            <label className="text-xs text-muted-foreground">SAP Name</label>
                            <input className="sc-input mt-1" value={editing.name || ""} disabled />
                        </div>

                        <div>
                            <label className="text-xs text-muted-foreground">Display Name</label>
                            <input
                                className="sc-input mt-1"
                                value={editing.display_name || ""}
                                onChange={(e) => setEditing({ ...editing, display_name: e.target.value })}
                                placeholder={editing.name || editing.warehouse_code}
                                data-testid="warehouse-display-name-input"
                            />
                        </div>

                        <div>
                            <label className="text-xs text-muted-foreground">Short Name</label>
                            <input
                                className="sc-input mt-1"
                                value={editing.short_name || ""}
                                onChange={(e) => setEditing({ ...editing, short_name: e.target.value })}
                                placeholder={editing.display_name || editing.name || editing.warehouse_code}
                                data-testid="warehouse-short-name-input"
                            />
                        </div>

                        <div>
                            <label className="text-xs text-muted-foreground">Sort Order</label>
                            <input
                                type="number"
                                className="sc-input mt-1"
                                value={editing.sort_order ?? 999}
                                onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })}
                                data-testid="warehouse-sort-order-input"
                            />
                        </div>

                        <div>
                            <label className="text-xs text-muted-foreground">
                                Item Groups carried by this warehouse
                                <span className="ml-1">— leave empty to show all items (no scoping)</span>
                            </label>
                            <div className="mt-1 max-h-52 overflow-y-auto border border-border rounded p-2 grid grid-cols-2 gap-1">
                                {itemGroups.length === 0 && <div className="text-xs text-muted-foreground col-span-2">No item groups loaded.</div>}
                                {itemGroups.map((g) => (
                                    <label key={g.code} className="flex items-center gap-2 text-xs cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={(editing.item_groups || []).includes(g.code)}
                                            onChange={() => toggleGroup(g.code)}
                                            data-testid={`warehouse-group-${g.code}`}
                                        />
                                        <span className="truncate">{g.name} <span className="text-muted-foreground font-mono">#{g.code}</span></span>
                                    </label>
                                ))}
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-1">
                                Scoped warehouses only list these product groups in Inquiry — plus any item that physically has stock here.
                            </div>
                        </div>

                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                checked={!!editing.active}
                                onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                                data-testid="warehouse-active-input"
                            />
                            Active
                        </label>

                        <div className="flex justify-end gap-2 pt-2 border-t border-border">
                            <button onClick={() => setEditing(null)} className="h-9 px-3 rounded border border-border text-sm">
                                Cancel
                            </button>
                            <button onClick={save} className="h-9 px-3 inline-flex items-center gap-1 rounded bg-foreground text-background text-sm" data-testid="warehouse-save-button">
                                <Save className="w-4 h-4" /> Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
